import { Stack } from "@mui/material";
import PropTypes from "prop-types";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import {
  BASE_ROUTE,
  CONSUMER_DASHBOARD_ROUTE,
  CONSUMER_USER,
  LEGAL_NOTICE_ROUTE,
  LOGIN_ROUTE,
  LOGIN_VERIFICATION_METHOD_ROUTE,
  LOG_IN,
  MY_PROFILE_ACCOUNT_SETTINGS_ROUTE,
  SECURITY_PREFERENCES_ROUTE,
} from "../../Constants/CommonConstants";
import {
  SLICE_LOGIN,
  THUNK_PUT_SELF_ENABLE,
} from "../../Constants/SliceAndThunkConstants";
import {
  ADD_OR_EDIT_VERIFICATION,
  MFA_METHOD_REGISTRATION_SUCCESSFUL,
  USER_ENABLE_SUCCESS,
  VALID_OTP,
} from "../../Constants/VerifyAccountConstants";
import {
  clearLoginApiResponse,
  resetIsUserVerificationRemembered,
  resetLoginValidateOTPDescription,
} from "../../Features/LoginSlice";
import { manageApiResponseSelector } from "../../Selectors/ApiResponseSelector";
import { manageLoginSelector } from "../../Selectors/LoginSelector";
import { manageVerificationSelector } from "../../Selectors/VerificationSelector";
import { VerifyAccountOTPField } from "./VerifyAccountOTPField";
import { VerifyAccountOTPLabel } from "./VerifyAccountOTPLabel";
import { navigateToFpactPage } from "../../Services/CommonServices";
import { manageAuthorizationSelector } from "../../Selectors/AuthorizationSelector";
import { clearAddEditEmailLanguage } from "../../Features/MyProfileSlice";

/**
 * @description Displays the Verify Account OTP Input Field
 * @param {string} previousScreen specifies the previous screen for verify account
 * @param {string} selectedVerificationMethod specifies the selected verification method
 * @returns {ReactNode} Renders the OTP Input field to the user
 */
export const VerifyAccountOTPFieldWrapper = ({
  previousScreen,
  selectedVerificationMethod,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { validateOTPDescription, email, phoneNo } = useSelector((state) =>
    manageVerificationSelector(state, previousScreen)
  );

  const { isUserVerificationRemembered, accountDetailsReceived, lnr } =
    useSelector((state) => manageLoginSelector(state));

  const { apiResponses } = useSelector((state) =>
    manageApiResponseSelector(state, SLICE_LOGIN, THUNK_PUT_SELF_ENABLE)
  );

  const { scopes } = useSelector((state) => manageAuthorizationSelector(state));

  const { isSuccess, description } = apiResponses;

  useEffect(() => {
    if (isSuccess && description === USER_ENABLE_SUCCESS) {
      navigate(`${BASE_ROUTE}${LOGIN_ROUTE}`);
      dispatch(resetLoginValidateOTPDescription());
    }
    dispatch(clearLoginApiResponse(THUNK_PUT_SELF_ENABLE));
  }, [description, isSuccess, navigate, dispatch]);

  const isBothVerificationsRegistered = email && phoneNo && previousScreen === LOG_IN;

  useEffect(() => {
    if (
      accountDetailsReceived &&
      ((validateOTPDescription === VALID_OTP && previousScreen === LOG_IN) ||
        (validateOTPDescription === MFA_METHOD_REGISTRATION_SUCCESSFUL &&
          previousScreen === ADD_OR_EDIT_VERIFICATION))
    ) {
      if (!isUserVerificationRemembered && !(isBothVerificationsRegistered)) {
        navigate(`/${LOGIN_ROUTE}/${LOGIN_VERIFICATION_METHOD_ROUTE}`, {
          replace: true,
        });
        dispatch(clearAddEditEmailLanguage());
      }
      if (
        isUserVerificationRemembered ||
        isBothVerificationsRegistered
      ) {
        scopes === CONSUMER_USER
          ? navigateToFpactPage(CONSUMER_DASHBOARD_ROUTE)
          : navigate(`/${LOGIN_ROUTE}/${LEGAL_NOTICE_ROUTE}`);
      }
      dispatch(resetIsUserVerificationRemembered());
      dispatch(resetLoginValidateOTPDescription());
    }
    if (
      validateOTPDescription === MFA_METHOD_REGISTRATION_SUCCESSFUL &&
      previousScreen === SECURITY_PREFERENCES_ROUTE
    ) {
      navigate(
        `/${MY_PROFILE_ACCOUNT_SETTINGS_ROUTE}/${SECURITY_PREFERENCES_ROUTE}`
      );
      dispatch(resetLoginValidateOTPDescription());
      dispatch(clearAddEditEmailLanguage());
    }
  }, [isUserVerificationRemembered, navigate, dispatch, validateOTPDescription, previousScreen, accountDetailsReceived, lnr, scopes, email, phoneNo, isBothVerificationsRegistered]);

  return (
    <Stack spacing={{ md: 2, xs: 3 }}>
      <VerifyAccountOTPLabel
        previousScreen={previousScreen}
        selectedVerificationMethod={selectedVerificationMethod}
      />
      <VerifyAccountOTPField previousScreen={previousScreen} />
    </Stack>
  );
};

VerifyAccountOTPFieldWrapper.propTypes = {
  /**
   * specifies the previous screen
   */
  previousScreen: PropTypes.string.isRequired,
  /**
   * specifies the previous screen
   */
  selectedVerificationMethod: PropTypes.string.isRequired,
};
